import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Link,
  Paper,
  Alert,
  CircularProgress,
  Container,
  Stepper,
  Step,
  StepLabel,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  InputAdornment,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { authService } from '../../services/authService';
import { apiRequest } from '../../config/api';
import { useTenant } from '../../contexts/TenantContext';

interface SignUpProps {
  onSignIn?: () => void;
}

type SignUpStep = 'info' | 'password' | 'verification';

export const SignUp: React.FC<SignUpProps> = ({ onSignIn }) => {
  const { config } = useTenant();
  const [step, setStep] = useState<SignUpStep>('info');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  // True when finishing an earlier, unconfirmed signup (account already exists;
  // we collect the password so we can auto-sign-in after verification).
  const [recoveryMode, setRecoveryMode] = useState(false);

  const { signIn, refreshProfile, user, profile, signOut } = useAuth();

  const existingAccountLabel =
    (profile as any)?.email ||
    (user as any)?.email ||
    (user as any)?.attributes?.email ||
    '';

  const handleSignOutExisting = async () => {
    try {
      await signOut();
    } catch {
      /* ignore */
    }
  };
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Pre-fill email from invitation token
  React.useEffect(() => {
    if (inviteToken) {
      fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/invitations/${inviteToken}`)
        .then(r => r.json())
        .then(data => {
          if (data.valid) {
            if (data.email) setEmail(data.email);
            if (data.firstName) setFirstName(data.firstName);
          }
        })
        .catch(() => {});
    }
  }, [inviteToken]);

  const steps = ['Your Info', 'Password', 'Verify'];

  const passwordRules = [
    { label: 'At least 8 characters', test: (p: string) => p.length >= 8 },
    { label: 'An uppercase letter (A-Z)', test: (p: string) => /[A-Z]/.test(p) },
    { label: 'A lowercase letter (a-z)', test: (p: string) => /[a-z]/.test(p) },
    { label: 'A number (0-9)', test: (p: string) => /\d/.test(p) },
    { label: 'A special character (!@#$%^&* etc.)', test: (p: string) => /[^a-zA-Z0-9]/.test(p) },
  ];

  const getPasswordStrength = (pwd: string): number => {
    if (!pwd) return 0;
    const passed = passwordRules.filter(r => r.test(pwd)).length;
    return (passed / passwordRules.length) * 100;
  };

  const getPasswordStrengthLabel = (strength: number): string => {
    if (strength < 40) return 'Weak';
    if (strength < 70) return 'Fair';
    if (strength < 90) return 'Good';
    return 'Strong';
  };

  const getPasswordStrengthColor = (strength: number): string => {
    if (strength < 40) return 'error';
    if (strength < 70) return 'warning';
    if (strength < 90) return 'info';
    return 'success';
  };

  const validateInfo = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePassword = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!password) {
      newErrors.password = 'Password is required';
    } else {
      const failedRules = passwordRules.filter(r => !r.test(password));
      if (failedRules.length > 0) {
        newErrors.password = `Password must contain: ${failedRules.map(r => r.label.toLowerCase()).join(', ')}`;
      }
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!acceptedTerms) {
      newErrors.terms = 'You must accept the terms of service';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInfoNext = async () => {
    setErrorMessage('');
    setInfoMessage('');
    setRecoveryMode(false);

    if (!validateInfo()) {
      return;
    }

    // If this email already has an UNCONFIRMED account (an earlier, interrupted
    // attempt), don't dead-end. A resend probe reliably distinguishes the cases
    // (PreventUserExistenceErrors is off): success = unconfirmed account,
    // "already confirmed" = existing account, UserNotFound = brand new. We send
    // an unconfirmed user to the Password step (recoveryMode) so we have her
    // password to auto-sign-in after she enters the code — without it, the
    // verify step can't sign her in and dead-ends at the sign-in screen.
    setLoading(true);
    try {
      await authService.resendConfirmationCode(email);
      setLoading(false);
      setRecoveryMode(true);
      setInfoMessage(
        "Looks like you already started signing up — we've emailed a fresh verification code. Enter the password you created, then the code, to finish.",
      );
      setStep('password');
      return;
    } catch (probeErr: any) {
      setLoading(false);
      const msg = probeErr?.message || '';
      if (/already confirmed|InvalidParameterException/i.test(msg)) {
        setErrorMessage('An account with this email already exists. Please sign in instead.');
        return;
      }
      if (/LimitExceeded|attempt.*exceeded|too many/i.test(msg)) {
        // Account exists (unconfirmed) but resends are rate-limited.
        setRecoveryMode(true);
        setInfoMessage(
          "You already have a verification code in your email (check spam). Enter the password you created, then that code, to finish.",
        );
        setStep('password');
        return;
      }
      // UserNotFoundException (new user) or other → continue to set a password.
    }

    // New user: fire prelink asynchronously so Salesforce lookup starts early
    void authService.prelinkMember({
      firstName,
      lastName,
      email,
      requestSource: 'member_web_signup',
    });

    setStep('password');
  };

  const handleCreateAccount = async () => {
    setErrorMessage('');
    setInfoMessage('');

    if (!validatePassword()) {
      return;
    }

    // Recovery: the account already exists (unconfirmed) and the code was
    // already sent — skip signUp and go straight to Verify. We keep the
    // password she just entered so handleVerification can sign her in.
    if (recoveryMode) {
      setInfoMessage('Enter the verification code we emailed you to finish.');
      setStep('verification');
      return;
    }

    setLoading(true);

    try {
      await authService.signUp(email, password, {
        given_name: firstName,
        family_name: lastName,
      });

      setStep('verification');
    } catch (error) {
      console.error('Sign up error:', error);
      if (error instanceof Error) {
        if (error.message.includes('UsernameExistsException')) {
          // The email belongs to an UNCONFIRMED account from an earlier,
          // interrupted attempt. Resend the code and resume at Verify so the
          // user is never dead-ended. Only an already-CONFIRMED account is sent
          // to sign-in; for anything else (incl. rate-limited resends) we still
          // route to Verify, where she can use an earlier code or tap Resend.
          try {
            await authService.resendConfirmationCode(email);
            setInfoMessage(
              "Looks like you already started signing up. We've sent a fresh verification code to your email — enter it below to finish.",
            );
            setStep('verification');
          } catch (resendErr: any) {
            const msg = resendErr?.message || '';
            if (/already confirmed|InvalidParameterException/i.test(msg)) {
              setErrorMessage('An account with this email already exists. Please sign in instead.');
            } else {
              setInfoMessage(
                'Enter the verification code we emailed you (check your spam folder), or tap “Resend code”.',
              );
              setStep('verification');
            }
          }
        } else if (error.message.includes('InvalidPasswordException') || error.message.includes('Password did not conform')) {
          setErrorMessage('Password does not meet requirements. It must be at least 8 characters and include an uppercase letter, lowercase letter, number, and special character.');
        } else {
          setErrorMessage(error.message);
        }
      } else {
        setErrorMessage('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    if (!verificationCode.trim()) {
      setErrors({ verificationCode: 'Verification code is required' });
      return;
    }

    setLoading(true);

    try {
      await authService.confirmSignUp(email, verificationCode);

      // Auto sign in after successful verification. If this is a recovery from
      // an interrupted earlier attempt, the password typed now can differ from
      // the original — sign-in then fails even though the account is verified.
      // Send the user to sign-in rather than showing a raw error.
      try {
        await signIn(email, password);
      } catch (signInErr) {
        console.warn('Auto sign-in after verification failed:', signInErr);
        setLoading(false);
        setInfoMessage('Your account is verified! Please sign in with your password to continue.');
        handleSignIn();
        return;
      }

      // Call register endpoint to create member doc + profileLink
      try {
        await authService.register({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim().toLowerCase(),
        });
      } catch (regError) {
        console.warn('Register call failed (non-blocking):', regError);
      }

      // Redeem invitation token if present (auto-vouch -> verifies the member).
      if (inviteToken) {
        try {
          await apiRequest('/api/invitations/' + inviteToken + '/redeem', { method: 'POST' });
          console.log('Invitation redeemed successfully');
        } catch (redeemErr) {
          console.warn('Invitation redemption failed (non-blocking):', redeemErr);
        }
      }

      // The member doc (and its now-verified status from redemption) was created
      // *after* the signIn above read a null profile. Re-load it into auth state
      // so the home screen reflects the verified profile instead of stale-null
      // (which would wrongly trigger a profile-claim prompt).
      try {
        await refreshProfile();
      } catch (refreshErr) {
        console.warn('Profile refresh after signup failed (non-blocking):', refreshErr);
      }

      // Redirect to home
      navigate('/home');
    } catch (error) {
      console.error('Verification error:', error);
      if (error instanceof Error) {
        if (error.message.includes('CodeMismatchException')) {
          setErrorMessage('Invalid verification code. Please try again.');
        } else if (error.message.includes('ExpiredCodeException')) {
          setErrorMessage('Verification code has expired. Please request a new one.');
        } else {
          setErrorMessage(error.message);
        }
      } else {
        setErrorMessage('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    setErrorMessage('');

    try {
      await authService.resendConfirmationCode(email);
      setErrorMessage('');
      // Show success message
      alert('Verification code sent! Please check your email.');
    } catch (error) {
      console.error('Resend code error:', error);
      setErrorMessage('Failed to resend code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = () => {
    if (onSignIn) {
      onSignIn();
    } else {
      navigate('/signin');
    }
  };

  const passwordStrength = getPasswordStrength(password);

  return (
    <Container
      maxWidth="sm"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: isMobile ? 2 : 4,
      }}
    >
      <Paper
        elevation={isMobile ? 0 : 3}
        sx={{
          p: isMobile ? 3 : 4,
          width: '100%',
          borderRadius: isMobile ? 0 : 2,
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" color="primary" gutterBottom fontWeight={600}>
            {config.appName}
          </Typography>
          <Typography variant="h5" color="text.primary" gutterBottom>
            Create Account
          </Typography>
        </Box>

        <Stepper
          activeStep={steps.indexOf(
            step === 'info' ? 'Your Info' : step === 'password' ? 'Password' : 'Verify',
          )}
          sx={{ mb: 4 }}
        >
          {steps.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {user && (
          <Alert
            severity="info"
            sx={{ mb: 3 }}
            action={
              <Button color="inherit" size="small" onClick={handleSignOutExisting}>
                Sign out
              </Button>
            }
          >
            You’re already signed in{existingAccountLabel ? ` as ${existingAccountLabel}` : ''}.
            Sign out first to create a different account.
          </Alert>
        )}

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErrorMessage('')}>
            {errorMessage}
          </Alert>
        )}

        {infoMessage && (
          <Alert severity="info" sx={{ mb: 3 }} onClose={() => setInfoMessage('')}>
            {infoMessage}
          </Alert>
        )}

        {/* Step 1: Your Info */}
        {step === 'info' && (
          <Box>
            <TextField
              fullWidth
              label="First Name"
              value={firstName}
              onChange={e => {
                setFirstName(e.target.value);
                if (errors.firstName) {
                  const { firstName: _, ...rest } = errors;
                  setErrors(rest);
                }
              }}
              error={!!errors.firstName}
              helperText={errors.firstName || ''}
              margin="normal"
              autoComplete="given-name"
              autoFocus
              disabled={loading}
            />

            <TextField
              fullWidth
              label="Last Name"
              value={lastName}
              onChange={e => {
                setLastName(e.target.value);
                if (errors.lastName) {
                  const { lastName: _, ...rest } = errors;
                  setErrors(rest);
                }
              }}
              error={!!errors.lastName}
              helperText={errors.lastName || ''}
              margin="normal"
              autoComplete="family-name"
              disabled={loading}
            />

            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={e => {
                setEmail(e.target.value);
                if (errors.email) {
                  const { email: _, ...rest } = errors;
                  setErrors(rest);
                }
              }}
              error={!!errors.email}
              helperText={errors.email || 'Use the email associated with your membership'}
              margin="normal"
              autoComplete="email"
              disabled={loading}
            />

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleInfoNext}
              disabled={loading}
              sx={{ mt: 2, mb: 2, py: 1.5 }}
            >
              Next
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Already have an account?{' '}
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={handleSignIn}
                  sx={{ cursor: 'pointer', fontWeight: 600 }}
                  disabled={loading}
                >
                  Sign In
                </Link>
              </Typography>
            </Box>
          </Box>
        )}

        {/* Step 2: Password */}
        {step === 'password' && (
          <Box>
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={e => {
                setPassword(e.target.value);
                if (errors.password) {
                  const { password: _, ...rest } = errors;
                  setErrors(rest);
                }
              }}
              error={!!errors.password}
              helperText={errors.password || ''}
              margin="normal"
              autoComplete="new-password"
              autoFocus
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowPassword(s => !s)}
                      edge="end"
                      tabIndex={-1}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {password && (
              <Box sx={{ mt: 1, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Password Strength
                  </Typography>
                  <Typography variant="caption" color={getPasswordStrengthColor(passwordStrength)}>
                    {getPasswordStrengthLabel(passwordStrength)}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={passwordStrength}
                  color={getPasswordStrengthColor(passwordStrength) as any}
                  sx={{ height: 6, borderRadius: 3, mb: 1 }}
                />
                <Box sx={{ pl: 0.5 }}>
                  {passwordRules.map(rule => {
                    const passed = rule.test(password);
                    return (
                      <Typography
                        key={rule.label}
                        variant="caption"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          color: passed ? 'success.main' : 'text.disabled',
                          lineHeight: 1.8,
                        }}
                      >
                        {passed ? '\u2713' : '\u2022'} {rule.label}
                      </Typography>
                    );
                  })}
                </Box>
              </Box>
            )}

            {!password && (
              <Box sx={{ mt: 1, mb: 1, px: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Password must contain:
                </Typography>
                <Box sx={{ pl: 0.5 }}>
                  {passwordRules.map(rule => (
                    <Typography
                      key={rule.label}
                      variant="caption"
                      color="text.disabled"
                      sx={{ display: 'block', lineHeight: 1.8 }}
                    >
                      {'\u2022'} {rule.label}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

            <TextField
              fullWidth
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={e => {
                setConfirmPassword(e.target.value);
                if (errors.confirmPassword) {
                  const { confirmPassword: _, ...rest } = errors;
                  setErrors(rest);
                }
              }}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword || ''}
              margin="normal"
              autoComplete="new-password"
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowConfirmPassword(s => !s)}
                      edge="end"
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={acceptedTerms}
                  onChange={e => {
                    setAcceptedTerms(e.target.checked);
                    if (errors.terms) {
                      const { terms: _, ...rest } = errors;
                      setErrors(rest);
                    }
                  }}
                  disabled={loading}
                />
              }
              label={
                <Typography variant="body2">
                  I accept the{' '}
                  <Link href="/terms" target="_blank">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link href="/privacy" target="_blank">
                    Privacy Policy
                  </Link>
                </Typography>
              }
              sx={{ mt: 2, mb: 1 }}
            />
            {errors.terms && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mb: 2 }}>
                {errors.terms}
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={() => setStep('info')}
                disabled={loading}
              >
                Back
              </Button>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleCreateAccount}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Create Account'}
              </Button>
            </Box>
          </Box>
        )}

        {/* Step 3: Verification */}
        {step === 'verification' && (
          <form onSubmit={handleVerification}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              We've sent a verification code to <strong>{email}</strong>. Please enter it below to
              verify your account.
            </Typography>

            <TextField
              fullWidth
              label="Verification Code"
              value={verificationCode}
              onChange={e => {
                setVerificationCode(e.target.value);
                if (errors.verificationCode) {
                  const { verificationCode: _, ...rest } = errors;
                  setErrors(rest);
                }
              }}
              error={!!errors.verificationCode}
              helperText={errors.verificationCode || ''}
              margin="normal"
              autoFocus
              disabled={loading}
              inputProps={{ maxLength: 6 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ mt: 3, mb: 2, py: 1.5 }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify Account'}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Didn't receive the code?{' '}
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={handleResendCode}
                  sx={{ cursor: 'pointer', fontWeight: 600 }}
                  disabled={loading}
                >
                  Resend Code
                </Link>
              </Typography>
            </Box>
          </form>
        )}
      </Paper>
    </Container>
  );
};

export default SignUp;
